/****************************************************
 * Module Name:  createExcelFile.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import * as XLSX from "sheetjs-style";
import Constants from "../../utility/constants";

export const createExcelFile = async (
    excelData,
    customerId,
    columns,
    filename,
) => {

    const COLORS = {
        headerBg: "FF4F81BD",
        headerText: "FFFFFFFF",
        totalBg: "FFEFEFEF",
        default: "000000",
    };

    // Determine background color for the STATUS cell based o
    // n user and item data
    const getStatusBackgroundColor = (item) => {
        const greenColor = "FFB6D7A8";
        const yellowColor = "FFF1E68C";
        const purpleColor = "FFB6B9D6";
        const skyBlueColor = "FFCAF7F6";
        const whiteColor = "D9FFFFFF";

        let color = (Constants.loggedInUser.usertype === "E" && item["holdstonecustomerid"] === customerId.value && item["holdstoneuserid"] === Constants.loggedInUser.id && item["STATUS"] === "HOLD") ||
            (Constants.loggedInUser.usertype === "C" && item["holdstonecustomerid"] === customerId.value && item["holdstonecustomerid"] === Constants.loggedInUser.id
                && item["holdstoneuserid"] === Constants.loggedInUser.id && item["STATUS"] === "HOLD") ||
            (Constants.loggedInUser.usertype === "B" && item["holdstonecustomerid"] === customerId.value && item["holdstonebrokerid"] === Constants.loggedInUser.id && item["STATUS"] === "HOLD") ? greenColor :
            ((item["holdstoneuserid"] === Constants.loggedInUser.id) && (item["holdstonecustomerid"] !== customerId.value) && item["STATUS"] === "HOLD") ||
                ((item["holdstonebrokerid"] === Constants.loggedInUser.id) && (item["holdstonecustomerid"] !== customerId.value) && item["STATUS"] === "HOLD") ? yellowColor :
                item["STATUS"] === "HOLD" ? purpleColor : item["STATUS"] === "MEMO_OUT" ? skyBlueColor : whiteColor;

        return color;
    };

    // Prepare data for export based on provided columns
    const columnKeys = columns.map(col => col.key); // will display just the columns with keys
    const filteredData = excelData.map(row =>
        Object.fromEntries(
            columnKeys.map(key => [key, row[key]])
        )
    );

    // Convert filtered JSON data to sheet
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    worksheet["!cols"] = columns.map(col => ({ wch: col.width || 10 }));

    // Add header row manually
    columns.forEach((col, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });

        // Default header style
        let cellStyle = {
            fill: { patternType: "solid", fgColor: { rgb: COLORS.headerBg } },
            font: { bold: true, color: { rgb: COLORS.headerText }, name: "Cambria", sz: 11, },
            alignment: { horizontal: "center" },
            border: {
                top: { style: "thin", color: { rgb: COLORS.default } },
                bottom: { style: "thin", color: { rgb: COLORS.default } },
                left: { style: "thin", color: { rgb: COLORS.default } },
                right: { style: "thin", color: { rgb: COLORS.default } },
            },
        };

        worksheet[cellRef] = {
            t: "s",
            v: col.label,
            s: cellStyle,
        };
    });

    // Process each data row for formatting and styling
    excelData.forEach((item, rowIndex) => {
        const actualRow = rowIndex + 1;
        const isTotalRow = rowIndex === excelData.length - 1;
        let isBold = false;
        Object.keys(item).forEach((key, colIndex) => {

            const value = item[key];
            const isNumeric = !isNaN(value) && value !== "" && value !== null;

            const cellRef = XLSX.utils.encode_cell({ r: actualRow, c: colIndex });
            worksheet[cellRef] = worksheet[cellRef] || {};

            let fillStyle = {};

            // logic for making cutpolsym bold
            if (key === "CUT" && item && !isTotalRow) {
                isBold = ["ID", "EX"].includes(item["CUT"]) &&
                    ["ID", "EX"].includes(item["POL"]) &&
                    ["ID", "EX"].includes(item["SYM"]);
            }
            // logic for highlighting status with color
            else if (key === "STATUS" && item && !isTotalRow) {
                fillStyle = {
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: customerId ? getStatusBackgroundColor(item) : "default" },
                    },
                };
            }
            // logic for last row's value and style and formula fromatting
            else if (isTotalRow) {

                fillStyle = {
                    fill: {
                        patternType: "solid",
                        fgColor: { rgb: COLORS.totalBg },
                    },
                };

                const totalCol = columns.findIndex(col => col.key === "TOTAL");
                const ctsCol = columns.findIndex(col => col.key === "CTS");
                const priceCol = columns.findIndex(col => col.key === "PPC");
                const disCol = columns.findIndex(col => col.key === "DIS");

                //  excel formulas for last record
                const columnLetter = XLSX.utils.encode_col(colIndex);
                if (key === "ID") {
                    worksheet[cellRef].f = `COUNTA(${columnLetter}${2}:${columnLetter}${excelData.length})`;
                    worksheet[cellRef].v = Number(item["ID"]);
                } else if (key === "CTS" || key === "TOTAL") {
                    worksheet[cellRef].f = `ROUND(SUM(${columnLetter}2:${columnLetter}${excelData.length}), 2)`;
                    worksheet[cellRef].v = key === "CTS" ? Number(item["CTS"]) : Number(item["TOTAL"]);
                } else if (key === "PPC") {
                    const totalLetter = XLSX.utils.encode_col(totalCol);
                    const ctsLetter = XLSX.utils.encode_col(ctsCol);

                    worksheet[cellRef].f = `IF(${ctsLetter}${excelData.length + 1}=0, 0, ROUND(${totalLetter}${excelData.length + 1}/${ctsLetter}${excelData.length + 1}, 2))`;
                    worksheet[cellRef].v = Number(item["PPC"]);
                } else if (key === "DIS") {

                    const discountLetter = XLSX.utils.encode_col(disCol);
                    const priceLetter = XLSX.utils.encode_col(priceCol);
                    const totalLetter = XLSX.utils.encode_col(totalCol);
                    const ctsLetter = XLSX.utils.encode_col(ctsCol);

                    worksheet[cellRef].f = `ROUND((100 - (SUM(${totalLetter}${2}:${totalLetter}${excelData.length}) / 
                        SUMPRODUCT(${ctsLetter}${2}:${ctsLetter}${excelData.length}, (100 * ${priceLetter}${2}:${priceLetter}${excelData.length}) 
                        / (100 + ${discountLetter}${2}:${discountLetter}${excelData.length})) )  * 100)  * -1,2)`

                    worksheet[cellRef].v = item["DIS"];
                }
            }

            // Format numbers as decimals (two places) for specific columns
            if (key === "CTS" || key === "TOTAL" || key === "PPC" || key === "DIS") {
                worksheet[cellRef].t = isNumeric ? "n" : "s";  // n for number, s for string
                if (isNumeric) {
                    worksheet[cellRef].z = "0.00"; // Excel number format with 2 decimal places
                }
            }

            // Apply cell-level styles
            worksheet[cellRef].s = {
                ...fillStyle,
                font: {
                    name: "Cambria",
                    sz: 11,
                    bold: isTotalRow || ((key === "CUT" || key === "POL" || key === "SYM") && isBold) || key === "DIS",
                    color: {
                        rgb: key === "MEDIA" ? "0061FF" : key === "DIS" ? "ED2939" : COLORS.default,
                    },
                    underline: key === "MEDIA" ? true : false,
                },
                alignment: { horizontal: "center" },
                border: {
                    top: { style: "thin", color: { rgb: COLORS.default } },
                    bottom: { style: "thin", color: { rgb: COLORS.default } },
                    left: { style: "thin", color: { rgb: COLORS.default } },
                    right: { style: "thin", color: { rgb: COLORS.default } },
                },
            };
        });
    });

    //initiate file download
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, worksheet, "Diamond Data");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });

    // browser download
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
